import { DateTime } from 'luxon';

import { crashReporterInterface, getLogger, tracker } from '../../utils/core';
import store from '../../utils/redux-store';
import * as actions from './bumpActions';
import * as bumpInterface from './bumpInterface';
import * as selectors from './bumpSelectors';
import {
  type BumpMessage,
  type BumpMessageData,
  CLOSE_BUMP_NEEDS_RECONNECTION,
  type SocketState,
  SocketStates,
} from './bumpTypes';

const log = getLogger('flux/bump/bumpInstance');

type BumpTrackingProps = {
  readyState?: number;
  type?: string;
  code?: number;
  reason?: string;
};

let pingPongTimer: ReturnType<typeof setTimeout> | null = null;
let timerToRecover: ReturnType<typeof setTimeout> | undefined;

let socket: WebSocket | null | undefined = null;
let socketBeingReconnected: WebSocket | null | undefined = null;

const getInjectedStore = (): NonNullable<typeof store> => {
  if (!store) {
    throw new Error('Redux store must be initialized before using Bump');
  }
  return store;
};

const getEventSocket = (event: Event): WebSocket | undefined =>
  event.currentTarget instanceof WebSocket ? event.currentTarget : undefined;

const isUnknownRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isOptionalString = (value: unknown): value is string | undefined =>
  value === undefined || typeof value === 'string';

const isBumpMessageData = (value: unknown): value is BumpMessageData =>
  isUnknownRecord(value) &&
  isOptionalString(value.jobType) &&
  isOptionalString(value.resourceType) &&
  (value.resources === undefined || Array.isArray(value.resources));

const isBumpMessage = (value: unknown): value is BumpMessage =>
  isUnknownRecord(value) &&
  isOptionalString(value.type) &&
  isOptionalString(value.eventType) &&
  (value.data === undefined || isBumpMessageData(value.data));

export const close = (code: number, context: string): void => {
  log.debug(`Bump WebSocket: Close requested [${code}] '${context}'`, new Date().toLocaleString());
  if (socket?.readyState === WebSocket.CONNECTING || socket?.readyState === WebSocket.OPEN) {
    log.debug(`Bump WebSocket: Close triggered [${code}] '${context}'`, new Date().toLocaleString());
    socket.close(code, context);
  }
};

export const open = (context = 'unknown'): void => {
  log.debug('Bump WebSocket: Open requested', context, new Date().toLocaleString());
  if (!socket) {
    log.debug('Bump WebSocket: Open triggered', context, new Date().toLocaleString());
    try {
      socket = bumpInterface.create();
    } catch (error) {
      log.error('Bump WebSocket: Failed to Open', error);

      // TODO: If we are offline, BumpComponent will re-issue open when back online so no need to implement
      //       a timeout here. This is likely a race condition if we are here and offline (open shouldn't be called when offline)
      //
      if (!bumpInterface.isOffline()) {
        const reconnectTimeout = Number(bumpInterface.reconnectTimeout());
        if (reconnectTimeout > 0) {
          setTimeout(() => {
            open('Automatic Retry on Failed Open');
          }, reconnectTimeout);
        }
      }
    }
    track({
      readyState: socket?.readyState,
    });
  }
};

export const send = (message: string): void => {
  log.debug('Bump WebSocket: Send ', new Date().toLocaleString(), message);
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(message);
  }
};

// This is required to handle case where socket gets closed but client doesn't receive an onclose message, perhaps
// due to a network error.
export const checkSocketConnection = (): void => {
  if (bumpInterface.isOffline()) {
    return;
  }

  const injectedStore = getInjectedStore();
  if (selectors.isBumpAuthenticed(injectedStore.getState())) {
    send('PING');
    pingPongTimer = setTimeout(() => {
      pingPongTimer = null;
      log.debug("Bump WebSocket: didn't receive pong, need to reconnect", new Date().toLocaleString());

      close(CLOSE_BUMP_NEEDS_RECONNECTION, 'Close from Checking Socket Connection');
    }, 3000);
  }
};

// =================================================================================================
// Socket Event Handlers
// =================================================================================================

const getSocketState = (readyState: number | undefined): SocketState => {
  switch (readyState) {
    case 0:
      return 'CONNECTING';
    case 1:
      return 'OPEN';
    case 2:
      return 'CLOSING';
    case 3:
      return 'CLOSED';
    default:
      log.error('Unhandled/Unknown Bump Socket State', readyState);
      return 'UNKNOWN';
  }
};

export const onClose: bumpInterface.BumpInterface['onClose'] = (event) => {
  const eventSocket = getEventSocket(event);
  const closingReconnectedSocket = eventSocket !== undefined && eventSocket === socketBeingReconnected;

  const timeOfClose = new Date().toLocaleString();
  log.debug(
    `Bump WebSocket: Closed, ${timeOfClose}, closingReconnectedSocket=${closingReconnectedSocket}`,
    event.code,
    event.reason,
  );

  if (closingReconnectedSocket) {
    socketBeingReconnected = undefined;
  } else {
    socket = undefined;
    getInjectedStore().dispatch(actions.setBumpSocketState(getSocketState(eventSocket?.readyState)));

    // bad idea to auto reconnect here - we might not have auth
    // const reconnectTimeout = bumpInterface.reconnectTimeout();
    // if (reconnectTimeout > 0 && !bumpInterface.isOffline()) {
    //   setTimeout(() => {
    //     if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
    //       open('Automatic Reconnect after Close');
    //     }
    //   }, reconnectTimeout);
    // }
  }

  track({
    readyState: eventSocket?.readyState,
    type: event.type,
    code: event.code,
    reason: closingReconnectedSocket ? 'Closing Reconnected Socket' : event.reason,
  });
};

export const onError: bumpInterface.BumpInterface['onError'] = (event) => {
  const eventSocket = getEventSocket(event);
  log.debug('Bump WebSocket: Error', new Date().toLocaleString(), event.code, event.reason);
  getInjectedStore().dispatch(actions.setBumpSocketState(SocketStates.ERROR));

  track({
    readyState: eventSocket?.readyState,
    type: event.type,
    code: event.code,
    reason: event.reason,
  });
};

export const onMessage: bumpInterface.BumpInterface['onMessage'] = (event) => {
  log.debug('Bump WebSocket: message received ', new Date().toLocaleString(), event.data);
  switch (event.data) {
    case '_AUTHENTICATED_':
      if (timerToRecover) {
        clearTimeout(timerToRecover);
        timerToRecover = undefined;
      }
      if (socketBeingReconnected) {
        socketBeingReconnected.close();
      }
      getInjectedStore().dispatch(actions.setBumpSocketState('AUTHENTICATED'));
      break;
    case '_AUTHENTICATION_FAILED_': {
      const reconnectTimeout = Number(bumpInterface.reconnectTimeout());
      timerToRecover = setTimeout(() => {
        const authSession = getInjectedStore().getState().authStore;
        crashReporterInterface.reportError(Error('Web Socket _AUTHENTICATION_FAILED_ - force re auth'), {
          custom: {
            now: DateTime.utc().toISO(),
            datasetId: authSession?.datasetId,
            hasAccessToken: Boolean(authSession?.accessToken),
            accessTokenExpired: authSession?.accessTokenExpired,
            hasRefreshToken: Boolean(authSession?.refreshToken),
            refreshTokenExpired: authSession?.refreshTokenExpired,
          },
        });

        verify(false, 'Web Socket _AUTHENTICATION_FAILED_ - force re auth');
        track({
          readyState: getEventSocket(event)?.readyState,
          type: event.type,
          reason: typeof event.data === 'string' ? event.data : undefined,
        });
      }, reconnectTimeout + 3000);
      // force re-auth
      // TODO: need QCS to tell us if dataset or access token is invalid
      bumpInterface.reAuth();
      break;
    }
    case '_CLOSE_': // server is requesting client to reconnect
      socketBeingReconnected = socket;
      socket = undefined;

      getInjectedStore().dispatch(actions.setBumpSocketState('RECONNECTING'));
      open('Server Requesting a Reconnect');
      break;
    case 'pong':
    case '_PONG_':
      if (pingPongTimer) {
        clearTimeout(pingPongTimer);
        pingPongTimer = null;
      }
      break;
    default:
      try {
        const message: unknown = typeof event.data === 'string' && event.data ? JSON.parse(event.data) : undefined;
        if (isBumpMessage(message)) {
          getInjectedStore().dispatch(actions.receivedBumpMessage(message));
        } else {
          log.warn('Unhandled (non-object) Bump Message', event.data);
        }
      } catch (error) {
        log.warn(
          'Unhandled (non-json) Bump Message',
          error instanceof Error ? error.toString() : String(error),
          event.data,
        );
      }
  }
};

export const onOpen: bumpInterface.BumpInterface['onOpen'] = (event) => {
  const eventSocket = getEventSocket(event);
  log.debug('Bump WebSocket: opened', new Date().toLocaleString(), eventSocket?.readyState);

  track({
    readyState: eventSocket?.readyState,
    type: event.type,
  });
  tracker.timeEvent(tracker.events.webSocket);

  getInjectedStore().dispatch(actions.setBumpSocketState(getSocketState(eventSocket?.readyState)));
};

const codes: Record<number, string> = {
  1000: 'Normal Closure',
  1001: 'Going Away',
  1002: 'Protocol Error',
  1003: 'Unsupported Data',
  1005: 'No Status Received',
  1006: 'Abnormal Closure',
  1007: 'Invalid frame payload data',
  1008: 'Policy Violation',
  1009: 'Message too big',
  1010: 'Missing Extension',
  1011: 'Internal Error',
  1012: 'Service Restart',
  1013: 'Try Again Later',
  1014: 'Bad Gateway',
  1015: 'TLS Handshake',
};

const track = (props: BumpTrackingProps): void => {
  const readyStateString = getSocketState(props.readyState);
  const codeString = props.code === undefined ? undefined : codes[props.code];
  tracker.track(tracker.events.webSocket, {
    ...props,
    readyStateString,
    codeString,
  });
};
